import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import NouveauReservationForm from '../components/NouveauReservationForm';
import ShareDocDialog from '../components/ShareDocDialog';
import { WhatsAppDevisButton } from '../components/WhatsAppDevisButton';
import {
  FileText, Receipt, ShoppingCart, CalendarCheck, ChevronRight, ArrowLeft,
  Palette, Video, LayoutPanelTop, Building2, FilePlus2, Download, Eye, Printer,
  TrendingUp, TrendingDown, X as XIcon, ArrowRightCircle, Plus, ListChecks, Send,
} from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, Tooltip as ReTooltip, XAxis } from 'recharts';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Mapping service Conception → sous-catégorie ────────────────────────────
function categoriseDevisConception(devis) {
  const svc = (devis.service_id || devis.service_titre || devis.modele || '').toLowerCase();
  if (svc.includes('supports') || svc.includes('imprim') || svc.includes('communication visuelle')) return 'conception';
  if (svc.includes('production') || svc.includes('vidéo') || svc.includes('video') || svc.includes('habillage') || svc.includes('signal')) return 'production';
  if (svc.includes('création') || svc.includes('creation') || svc.includes('entreprise')) return 'creation_entreprise';
  return 'conception';
}

// ─── Normalisation catégorie facture (client historique → panneaux) ─────────
function normaliseCatFacture(cat) {
  const c = (cat || '').toLowerCase().trim();
  if (['panneaux', 'conception', 'production', 'creation_entreprise'].includes(c)) return c;
  if (c === 'client' || c === '' || c.includes('panneau')) return 'panneaux';
  // Toutes les autres (fournisseur, charges_*, dette_*, achat, ...) → non listées ici
  return 'autre';
}

export default function SuiviHub({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [devisPanneaux, setDevisPanneaux] = useState([]);
  const [devisConception, setDevisConception] = useState([]);
  const [factures, setFactures] = useState([]);
  const [bonsCommande, setBonsCommande] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [listesCommande, setListesCommande] = useState([]);
  const [showReservationForm, setShowReservationForm] = useState(false);

  const load = async () => {
    try {
      const [dp, dc, f, bc, r, lc] = await Promise.all([
        axios.get(`${API}/api/devis`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/conception/devis`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/factures`).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/api/commandes`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/emplacement-reservations`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/listes-commande`).catch(() => ({ data: [] })),
      ]);
      setDevisPanneaux(Array.isArray(dp.data) ? dp.data : (dp.data.items || []));
      setDevisConception(dc.data || []);
      setFactures(f.data.items || f.data || []);
      setBonsCommande(bc.data || []);
      setReservations(r.data || []);
      setListesCommande(lc.data || []);
    } catch (e) { toast.error('Erreur de chargement'); }
  };

  useEffect(() => { load(); }, []);

  // Comptages
  const devisConcByCat = { conception: 0, production: 0, creation_entreprise: 0 };
  devisConception.forEach(d => { devisConcByCat[categoriseDevisConception(d)]++; });
  const facturesByCat = { conception: 0, production: 0, panneaux: 0, creation_entreprise: 0 };
  factures.forEach(f => {
    const cat = normaliseCatFacture(f.categorie);
    if (facturesByCat[cat] !== undefined) facturesByCat[cat]++;
  });

  const active = searchParams.get('type'); // 'devis' | 'facture' | 'bon_commande' | 'reservation' | null
  const sub = searchParams.get('sub');     // sous-catégorie
  const setActive = (t) => setSearchParams(t ? { type: t } : {});
  const setSub = (s) => setSearchParams({ type: active, sub: s });

  // Filtrage pour la vue liste
  const listItems = (() => {
    if (active === 'devis') {
      if (sub === 'panneaux') return devisPanneaux;
      if (sub === 'conception') return devisConception.filter(d => categoriseDevisConception(d) === 'conception');
      if (sub === 'production') return devisConception.filter(d => categoriseDevisConception(d) === 'production');
      if (sub === 'creation_entreprise') return devisConception.filter(d => categoriseDevisConception(d) === 'creation_entreprise');
    }
    if (active === 'facture') {
      return factures.filter(f => normaliseCatFacture(f.categorie) === (sub || 'panneaux'));
    }
    if (active === 'bon_commande') return bonsCommande;
    if (active === 'reservation') return reservations;
    if (active === 'liste_commande') return listesCommande;
    return [];
  })();

  return (
    <Layout user={user} onLogout={onLogout} currentPage="suivi">
      <div className="space-y-6" data-testid="suivi-hub">
        <div>
          <div className="text-xs text-gray-400 mb-1">
            <button onClick={() => setSearchParams({})} className="hover:underline">Accueil</button>
            <span className="mx-2">/</span>
            <span>Documents commerciaux</span>
          </div>
          <h1 className="text-3xl font-bold text-[#0B1E42]">Documents commerciaux</h1>
          <p className="text-sm text-gray-500">Gérez vos documents (devis, factures, bons de commande, réservations, commandes)</p>
        </div>

        {/* 4 cases compactes avec compteurs et sous-titres */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <CompactHubCard active={active === 'devis'} onClick={() => setActive('devis')} accent="from-blue-500 to-blue-600" icon={FileText} label="Devis" count={devisPanneaux.length + devisConception.length} testid="hub-devis" />
          <CompactHubCard active={active === 'facture'} onClick={() => setActive('facture')} accent="from-emerald-500 to-emerald-600" icon={Receipt} label="Factures" count={factures.length} testid="hub-facture" />
          <CompactHubCard active={active === 'bon_commande'} onClick={() => setActive('bon_commande')} accent="from-amber-500 to-amber-600" icon={ShoppingCart} label="Bon de commande" count={bonsCommande.length} testid="hub-bc" />
          <CompactHubCard active={active === 'reservation'} onClick={() => setActive('reservation')} accent="from-violet-500 to-violet-600" icon={CalendarCheck} label="Réservation" count={reservations.length} testid="hub-reservation" />
          <CompactHubCard active={active === 'liste_commande'} onClick={() => setActive('liste_commande')} accent="from-teal-500 to-teal-600" icon={ListChecks} label="Liste de commande" count={listesCommande.length} testid="hub-liste-commande" />
        </div>

        {/* Statistiques & tendances (visible uniquement sur la page d'accueil du hub) */}
        {!active && (
          <StatsSection
            devis={[...devisPanneaux, ...devisConception]}
            factures={factures}
            bonsCommande={bonsCommande}
            reservations={reservations}
          />
        )}

        {/* Sous-sélection sous-catégorie */}
        {!sub && active === 'devis' && (
          <SubCategoriesPanel title="DEVIS" icon={FileText} color="text-blue-600"
            items={[
              { key: 'conception', label: 'DEVIS CONCEPTION', desc: 'Devis pour la conception graphique et création de supports.', icon: Palette, count: devisConcByCat.conception, color: 'text-blue-600' },
              { key: 'production', label: 'DEVIS PRODUCTION', desc: 'Devis pour la production audiovisuelle et autres réalisations.', icon: Video, count: devisConcByCat.production, color: 'text-blue-600' },
              { key: 'panneaux', label: 'DEVIS PANNEAUX', desc: 'Devis pour la location et/ou la réalisation de panneaux publicitaires (LED, déroulants, grand format).', icon: LayoutPanelTop, count: devisPanneaux.length, color: 'text-blue-600' },
              { key: 'creation_entreprise', label: "DEVIS CRÉATION D'ENTREPRISE", desc: "Devis pour la création d'entreprise et formalités administratives.", icon: Building2, count: devisConcByCat.creation_entreprise, color: 'text-blue-600' },
              { key: 'autre', label: 'AUTRE DEVIS', desc: 'Autres types de devis personnalisés.', icon: FilePlus2, count: 0, color: 'text-blue-600' },
            ]}
            onPick={setSub}
          />
        )}
        {!sub && active === 'facture' && (
          <SubCategoriesPanel title="FACTURE" icon={Receipt} color="text-emerald-600"
            items={[
              { key: 'conception', label: 'FACTURE CONCEPTION', desc: 'Facture pour la conception graphique et création de supports.', icon: Palette, count: facturesByCat.conception, color: 'text-emerald-600' },
              { key: 'production', label: 'FACTURE PRODUCTION', desc: 'Facture pour la production audiovisuelle et autres réalisations.', icon: Video, count: facturesByCat.production, color: 'text-emerald-600' },
              { key: 'panneaux', label: 'FACTURE PANNEAUX', desc: 'Facture pour la location et/ou la réalisation de panneaux publicitaires (LED, déroulants, grand format).', icon: LayoutPanelTop, count: facturesByCat.panneaux, color: 'text-emerald-600' },
              { key: 'creation_entreprise', label: "FACTURE CRÉATION D'ENTREPRISE", desc: "Facture pour la création d'entreprise et formalités administratives.", icon: Building2, count: facturesByCat.creation_entreprise, color: 'text-emerald-600' },
              { key: 'autre', label: 'AUTRE FACTURE', desc: 'Autres types de factures personnalisées.', icon: FilePlus2, count: 0, color: 'text-emerald-600' },
            ]}
            onPick={setSub}
          />
        )}

        {/* Vue liste (sub sélectionnée ou catégorie directe sans sub) */}
        {active && (sub || active === 'bon_commande' || active === 'reservation' || active === 'liste_commande') && (
          <>
            {active === 'reservation' && (
              <div className="flex justify-end mb-3">
                <Button
                  onClick={() => setShowReservationForm(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                  data-testid="btn-nouvelle-reservation"
                >
                  <Plus className="h-4 w-4" /> Faire une réservation
                </Button>
              </div>
            )}
            <ListView type={active} sub={sub} items={listItems} onBack={() => setSearchParams({ type: active })} navigate={navigate} />
            <Sheet open={showReservationForm} onOpenChange={setShowReservationForm}>
              <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-6 bg-slate-50" data-testid="suivi-nouvelle-reservation-sheet">
                <SheetHeader className="mb-4">
                  <SheetTitle className="flex items-center gap-2 text-xl">
                    <CalendarCheck className="h-5 w-5 text-blue-600" />
                    Nouvelle réservation
                  </SheetTitle>
                  <SheetDescription>
                    Renseignez les infos de l'entreprise, choisissez le nombre d'emplacements et générez la réservation.
                  </SheetDescription>
                </SheetHeader>
                <NouveauReservationForm
                  onClose={() => setShowReservationForm(false)}
                  onCreated={() => { setShowReservationForm(false); load(); }}
                />
              </SheetContent>
            </Sheet>
          </>
        )}
      </div>
    </Layout>
  );
}

// ── Composants ─────────────────────────────────────────────────────────────
function CompactHubCard({ active, onClick, accent, icon: Icon, label, count, testid }) {
  return (
    <button
      onClick={onClick}
      className={`text-left bg-white border rounded-xl p-4 transition-all hover:shadow-lg hover:-translate-y-0.5 ${active ? 'ring-2 ring-blue-400 border-blue-400' : 'border-slate-200'}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${accent} flex items-center justify-center flex-shrink-0`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
          <div className="text-2xl font-black text-[#0B1E42] leading-none mt-0.5">{count}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">documents</div>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
      </div>
    </button>
  );
}

// ─── Groupe et compte les documents par mois sur les 6 derniers mois ─────────
function buildMonthlySeries(items, dateFieldCandidates = ['created_at', 'date_creation', 'date_facture', 'date']) {
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('fr-FR', { month: 'short' }),
      count: 0,
    });
  }
  items.forEach(it => {
    let dstr = null;
    for (const f of dateFieldCandidates) {
      if (it[f]) { dstr = String(it[f]); break; }
    }
    if (!dstr) return;
    const key = dstr.slice(0, 7);
    const m = months.find(x => x.key === key);
    if (m) m.count += 1;
  });
  return months;
}

function computeTrend(series) {
  const total = series.reduce((s, m) => s + m.count, 0);
  const last = series[series.length - 1]?.count || 0;
  const prev = series[series.length - 2]?.count || 0;
  let pct = 0;
  if (prev === 0 && last > 0) pct = 100;
  else if (prev > 0) pct = Math.round(((last - prev) / prev) * 100);
  return { total, last, prev, pct };
}

function StatsSection({ devis, factures, bonsCommande, reservations }) {
  const stats = useMemo(() => [
    { title: 'Évolution des devis', color: '#3b82f6', series: buildMonthlySeries(devis) },
    { title: 'Évolution des factures', color: '#10b981', series: buildMonthlySeries(factures, ['date_facture', 'created_at']) },
    { title: 'Évolution des bons de commande', color: '#f59e0b', series: buildMonthlySeries(bonsCommande) },
    { title: 'Évolution des réservations', color: '#8b5cf6', series: buildMonthlySeries(reservations) },
  ], [devis, factures, bonsCommande, reservations]);

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
          </div>
          <div>
            <div className="font-bold text-[#0B1E42]">Statistiques & tendances</div>
            <div className="text-xs text-slate-500">Évolution des documents sur les 6 derniers mois</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {stats.map(s => {
            const t = computeTrend(s.series);
            const positive = t.pct >= 0;
            return (
              <div key={s.title} className="border rounded-xl p-3 bg-white" data-testid={`stat-${s.title}`}>
                <div className="text-xs font-semibold text-slate-600 mb-1">{s.title}</div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-2xl font-black text-[#0B1E42]">{t.total}</span>
                  <span className={`text-xs font-bold ${positive ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {positive ? <TrendingUp className="h-3 w-3 inline" /> : <TrendingDown className="h-3 w-3 inline" />}
                    {' '}{positive ? '+' : ''}{t.pct}%
                  </span>
                </div>
                <div className="h-16 -mx-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={s.series}>
                      <XAxis dataKey="label" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} interval={0} />
                      <ReTooltip
                        contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #e5e7eb' }}
                        labelFormatter={(l) => `Mois : ${l}`}
                        formatter={(v) => [v, 'documents']}
                      />
                      <Line type="monotone" dataKey="count" stroke={s.color} strokeWidth={2} dot={{ r: 2, fill: s.color }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className={`text-[10px] mt-1 ${positive ? 'text-emerald-600' : 'text-slate-500'}`}>
                  {t.last === 0 ? 'Aucun document ce mois-ci' :
                    positive ? `📈 Hausse ce mois-ci (${t.last} document(s))` : `📉 Baisse ce mois-ci (${t.last} document(s))`}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function HubCard({ active, onClick, bg, iconBg, iconColor, icon: Icon, title, subtitle, testid }) {
  return (
    <button onClick={onClick} className={`text-left ${bg} border-2 rounded-2xl p-5 transition-all hover:shadow-lg ${active ? 'ring-2 ring-offset-2 ring-blue-400' : ''}`} data-testid={testid}>
      <div className={`w-14 h-14 rounded-xl ${iconBg} flex items-center justify-center mb-3`}>
        <Icon className={`h-7 w-7 ${iconColor}`} />
      </div>
      <div className="font-black text-lg text-[#0B1E42] tracking-wide">{title}</div>
      <div className="text-xs text-gray-500 mt-1">{subtitle}</div>
      <div className="text-right mt-2 text-blue-500"><ChevronRight className="inline-block h-5 w-5" /></div>
    </button>
  );
}

function SubCategoriesPanel({ title, icon: TitleIcon, color, items, onPick }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center`}>
            <TitleIcon className={`h-5 w-5 ${color}`} />
          </div>
          <div>
            <div className="font-bold text-[#0B1E42]">{title}</div>
            <div className="text-xs text-gray-500">Sélectionnez le type de {title.toLowerCase()} à créer</div>
          </div>
        </div>
        <div className="space-y-2">
          {items.map(it => {
            const Ic = it.icon;
            return (
              <button key={it.key} onClick={() => onPick(it.key)} className="w-full flex items-center gap-4 p-3 rounded-lg border hover:shadow-md hover:border-blue-300 transition text-left" data-testid={`sub-${it.key}`}>
                <div className="w-11 h-11 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
                  <Ic className={`h-5 w-5 ${it.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-bold text-sm ${it.color}`}>{it.label}</div>
                  <div className="text-xs text-gray-500 leading-tight">{it.desc}</div>
                </div>
                {it.count > 0 && <Badge className="bg-[#0B1E42] text-white">{it.count}</Badge>}
                <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function ListView({ type, sub, items, onBack, navigate }) {
  const [previewItem, setPreviewItem] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [shareItem, setShareItem] = useState(null);
  const isListe = type === 'liste_commande';

  // Filtrer items
  const filteredItems = items.filter(it => {
    // Recherche texte
    if (search.trim()) {
      const q = search.toLowerCase();
      const hay = [
        it.numero, it.numero_devis, it.client_nom, it.client?.nom,
        it.entreprise, it.prospect_entreprise, it.prospect_nom,
        it.fournisseur_nom, it.service_titre, it.objet, it.campagne
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    // Date range
    const dateStr = String(it.date_facture || it.date_creation || it.created_at || it.date || '').slice(0, 10);
    if (dateFrom && dateStr && dateStr < dateFrom) return false;
    if (dateTo && dateStr && dateStr > dateTo) return false;
    // Statut
    if (statusFilter) {
      const s = (it.statut || it.statut_paiement || '').toLowerCase();
      if (!s.includes(statusFilter.toLowerCase())) return false;
    }
    return true;
  });

  const title = (() => {
    if (type === 'bon_commande') return 'Bons de commande';
    if (type === 'reservation') return 'Réservations';
    if (type === 'liste_commande') return 'Listes de commande';
    const t = type === 'facture' ? 'Factures' : 'Devis';
    const map = { conception: 'Conception', production: 'Production', panneaux: 'Panneaux', creation_entreprise: "Création d'entreprise", autre: 'Autres' };
    return `${t} ${map[sub] || ''}`.trim();
  })();

  const getPdfUrl = (item) => {
    if (type === 'devis') {
      if (sub === 'panneaux') return `${API}/api/devis/${item.id}/pdf`;
      return `${API}/api/conception/devis/${item.id}/pdf`;
    }
    if (type === 'facture') return `${API}/api/factures/${item.id}/pdf?force_sesam=1`;
    if (type === 'bon_commande') return `${API}/api/commandes/${item.id}/pdf`;
    if (type === 'reservation') return `${API}/api/emplacement-reservations/${item.id}/pdf`;
    if (type === 'liste_commande') return `${API}/api/listes-commande/${item.id}/pdf`;
    return null;
  };

  const fetchBlob = async (item) => {
    const url = getPdfUrl(item);
    if (!url) return null;
    // Réservations : endpoint renvoie JSON {html} — on convertit en blob HTML
    if (type === 'reservation') {
      const r = await axios.get(url);
      const html = r.data?.html || '';
      if (!html) return null;
      return new Blob([html], { type: 'text/html' });
    }
    const r = await axios.get(url, { responseType: 'blob' });
    return new Blob([r.data], { type: 'application/pdf' });
  };

  const preview = async (item) => {
    setPreviewItem(item);
    setPreviewBlobUrl('');
    setPreviewLoading(true);
    try {
      const blob = await fetchBlob(item);
      if (!blob) { toast.info('Aperçu bientôt disponible'); setPreviewItem(null); return; }
      setPreviewBlobUrl(URL.createObjectURL(blob));
    } catch { toast.error('Erreur aperçu'); setPreviewItem(null); }
    finally { setPreviewLoading(false); }
  };

  const closePreview = () => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl('');
    setPreviewItem(null);
  };

  const convertToFacture = async (item) => {
    if (type !== 'devis') return;
    if (!window.confirm(`Transformer le devis ${item.numero || item.numero_devis} en facture ?\n\nLe devis sera conservé et une facture identique sera créée dans Suivi > Facture.`)) return;
    try {
      const isConception = sub && sub !== 'panneaux';
      const url = isConception
        ? `${API}/api/conception/devis/${item.id}/convert-to-facture`
        : `${API}/api/devis/${item.id}/convert-to-facture`;
      const r = await axios.post(url);
      if (r.data?.success) {
        toast.success(r.data.message || `Facture ${r.data.facture?.numero} créée`);
      } else {
        toast.error('Erreur lors de la conversion');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur serveur');
    }
  };

  const download = async (item) => {
    try {
      const blob = await fetchBlob(item);
      if (!blob) { toast.info('PDF bientôt disponible'); return; }
      const isHtml = blob.type?.includes('text/html');
      const ext = isHtml ? 'html' : 'pdf';
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${type}-${item.numero || item.numero_devis || item.id}.${ext}`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch { toast.error('Erreur téléchargement'); }
  };

  const printDoc = async (item) => {
    try {
      const blob = await fetchBlob(item);
      if (!blob) { toast.info('Impression bientôt disponible'); return; }
      const objUrl = URL.createObjectURL(blob);
      // Approche via iframe caché (fiable inter-navigateurs même avec un PDF viewer natif)
      let iframe = document.getElementById('__suivi_print_iframe');
      if (iframe) { iframe.remove(); }
      iframe = document.createElement('iframe');
      iframe.id = '__suivi_print_iframe';
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = objUrl;
      document.body.appendChild(iframe);
      const triggerPrint = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (e) {
          // Fallback : ouvrir dans un nouvel onglet
          const win = window.open(objUrl, '_blank');
          if (win) { setTimeout(() => { try { win.focus(); win.print(); } catch { /* ignore */ } }, 800); }
          else { toast.error('Bloqueur de pop-ups actif — autorisez-les pour imprimer'); }
        }
      };
      iframe.onload = () => setTimeout(triggerPrint, 500);
      // Fallback si onload ne se déclenche pas
      setTimeout(triggerPrint, 1800);
    } catch { toast.error('Erreur impression'); }
  };

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="outline" size="sm" onClick={onBack} className="gap-1" data-testid="list-back-btn"><ArrowLeft className="h-4 w-4" /> Retour</Button>
          <h2 className="font-bold text-lg text-[#0B1E42]">{title}</h2>
          <Badge className="bg-slate-100 text-slate-700">{filteredItems.length} / {items.length}</Badge>
        </div>

        {/* Barre de recherche + filtres */}
        <div className="mb-3 grid grid-cols-1 md:grid-cols-4 gap-2">
          <input
            type="text"
            placeholder="🔍 Rechercher (numéro, client, objet...)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="col-span-2 h-9 px-3 rounded-md border border-slate-200 text-sm bg-white focus:ring-2 focus:ring-blue-200"
            data-testid="list-search"
          />
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} placeholder="Du" className="h-9 px-2 rounded-md border border-slate-200 text-sm bg-white" title="Date de début" data-testid="list-date-from" />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} placeholder="Au" className="h-9 px-2 rounded-md border border-slate-200 text-sm bg-white" title="Date de fin" data-testid="list-date-to" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 px-2 rounded-md border border-slate-200 text-sm bg-white col-span-2 md:col-span-1"
            data-testid="list-status-filter"
          >
            <option value="">Tous statuts</option>
            <option value="brouillon">Brouillon</option>
            <option value="envoyé">Envoyé</option>
            <option value="paye">Payé</option>
            <option value="attente">En attente</option>
            <option value="retard">En retard</option>
            <option value="signe">Signé</option>
            <option value="accepté">Accepté</option>
          </select>
          {(search || dateFrom || dateTo || statusFilter) && (
            <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); setStatusFilter(''); }} className="text-slate-600" data-testid="list-clear-filters">
              ✕ Effacer filtres
            </Button>
          )}
        </div>
        {items.length === 0 ? (
          <p className="text-center py-12 text-gray-500">Aucun document dans cette catégorie.</p>
        ) : (
          <div className="space-y-2" data-testid="list-view">
            {items.map(it => (
              <div key={it.id} className="flex items-center justify-between gap-3 p-3 border rounded-lg hover:shadow-sm">
                {/* Boutons d'action à gauche */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button size="icon" variant="outline" onClick={() => preview(it)} title="Prévisualiser" data-testid={`btn-preview-${it.id}`} className="h-9 w-9 text-blue-600 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300">
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="outline" onClick={() => download(it)} title="Télécharger" data-testid={`btn-download-${it.id}`} className="h-9 w-9 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300">
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="outline" onClick={() => printDoc(it)} title="Imprimer" data-testid={`btn-print-${it.id}`} className="h-9 w-9 text-slate-600 hover:bg-slate-100 hover:text-slate-800">
                    <Printer className="h-4 w-4" />
                  </Button>
                  {isListe && (
                    <Button size="sm" onClick={() => setShareItem(it)} title="Envoyer via WhatsApp ou WeChat" data-testid={`btn-send-${it.id}`} className="h-9 gap-1 bg-teal-600 hover:bg-teal-700 text-white text-xs px-2">
                      <Send className="h-3.5 w-3.5" />
                      Envoyer
                    </Button>
                  )}
                  {type === 'devis' && (
                    <WhatsAppDevisButton item={it} kind={sub === 'panneaux' ? 'devis_panneaux' : 'devis_conception'} fetchBlob={fetchBlob} />
                  )}
                  {type === 'devis' && (
                    <Button size="sm" onClick={() => convertToFacture(it)} title="Transformer en facture (le devis reste)" data-testid={`btn-to-facture-${it.id}`} className="h-9 gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs px-2">
                      <ArrowRightCircle className="h-3.5 w-3.5" />
                      Facturer
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Badge className="bg-[#0B1E42] text-white">{it.numero || it.numero_devis || it.id?.slice(0, 8)}</Badge>
                  <span className="font-semibold text-sm truncate">{it.client_nom || it.entreprise || it.fournisseur_nom || it.client?.nom || it.prospect_entreprise || it.prospect_nom || '—'}</span>
                  <span className="text-xs text-gray-500 truncate">{it.service_titre || it.objet || ''}</span>
                </div>
                {isListe ? (
                  <div className="text-right" data-testid={`liste-qty-${it.id}`}>
                    <div className="text-xs text-gray-500">{it.nb_items} article(s)</div>
                    <div className="font-bold text-[#0B1E42]">Qté totale : {new Intl.NumberFormat('fr-FR').format(it.total_quantite || 0)}</div>
                  </div>
                ) : (
                <div className="text-right">
                  <div className="text-xs text-gray-500">Total TTC</div>
                  <div className="font-bold text-[#0B1E42]">{new Intl.NumberFormat('fr-FR').format(Math.round(it.total_ttc || it.total || it.montant || 0))} FCFA</div>
                </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Modal d'aperçu PDF inline */}
      <Dialog open={!!previewItem} onOpenChange={(v) => { if (!v) closePreview(); }}>
        <DialogContent className="max-w-5xl h-[90vh] p-0 flex flex-col overflow-hidden" data-testid="pdf-preview-dialog">
          <DialogHeader className="bg-[#0B1E42] text-white px-4 py-3 flex-shrink-0">
            <DialogTitle className="text-white flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <Eye className="h-4 w-4" /> Aperçu — {previewItem?.numero || previewItem?.numero_devis || previewItem?.id?.slice(0, 8)}
              </span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="secondary" onClick={() => previewItem && download(previewItem)} className="h-8 gap-1">
                  <Download className="h-3 w-3" /> Télécharger
                </Button>
                <Button size="sm" variant="secondary" onClick={() => previewItem && printDoc(previewItem)} className="h-8 gap-1">
                  <Printer className="h-3 w-3" /> Imprimer
                </Button>
                {isListe && (
                  <Button size="sm" onClick={() => previewItem && setShareItem(previewItem)} className="h-8 gap-1 bg-teal-500 hover:bg-teal-600 text-white" data-testid="preview-send-btn">
                    <Send className="h-3 w-3" /> Envoyer
                  </Button>
                )}
                <Button size="icon" variant="ghost" onClick={closePreview} className="text-white hover:bg-white/10 h-8 w-8">
                  <XIcon className="h-4 w-4" />
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 bg-slate-100 overflow-hidden">
            {previewLoading && (
              <div className="h-full flex items-center justify-center text-slate-500">
                <span className="animate-pulse">Chargement du document...</span>
              </div>
            )}
            {previewBlobUrl && (
              <iframe
                src={previewBlobUrl}
                title="Aperçu PDF"
                className="w-full h-full border-0"
                data-testid="pdf-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
      <ShareDocDialog
        open={!!shareItem}
        onOpenChange={(v) => { if (!v) setShareItem(null); }}
        kind="liste_commande"
        docId={shareItem?.id}
        docLabel={`la liste de commande ${shareItem?.numero || ''}`}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Formulaire de nouvelle réservation (panneau latéral droit)
// ─────────────────────────────────────────────────────────────
function ReservationForm({ onClose, onSaved }) {
  const [emplacements, setEmplacements] = useState([]);
  const [contrats, setContrats] = useState([]);
  const [form, setForm] = useState({
    entreprise: '', contact: '', responsable: '',
    type_panneau: 'Panneau déroulant',
    emplacement_id: '', nb_affiches: 1,
    date_debut: '', date_fin: '', motif: '', montant: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/emplacements`).then(r => setEmplacements(r.data || []));
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/contrats`).then(r => setContrats(r.data || [])).catch(() => {});
  }, []);

  const filteredEmps = emplacements.filter(e => (e.type_panneau || '').toLowerCase() === form.type_panneau.toLowerCase());

  // Détection panneau indisponible : réservations actives sur cet emplacement
  const isUnavailable = (emp) => {
    if (!emp) return false;
    const activeContrats = contrats.filter(c => !['Annulé', 'Refusé'].includes(c.statut || ''));
    for (const c of activeContrats) {
      for (const l of (c.lignes || [])) {
        const names = [...(l.emplacements || []), l.emplacement].filter(Boolean);
        if (names.some(n => (n || '').toLowerCase() === (emp.nom || '').toLowerCase())) return true;
      }
    }
    return false;
  };

  const selectedEmp = emplacements.find(e => e.id === form.emplacement_id);
  const empUnavailable = selectedEmp && isUnavailable(selectedEmp);

  const submit = async () => {
    if (!form.entreprise || !form.emplacement_id || !form.date_debut || !form.date_fin) {
      toast.error('Renseignez au minimum entreprise, emplacement, dates'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/emplacement-reservations`, {
        emplacement_id: form.emplacement_id,
        entreprise: form.entreprise,
        contact: form.contact,
        responsable: form.responsable,
        motif: form.motif,
        date_debut: form.date_debut,
        date_fin: form.date_fin,
        montant: form.montant,
        notes: form.notes,
        nb_affiches: parseInt(form.nb_affiches, 10) || 1,
      });
      toast.success('Réservation créée avec succès');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur création'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed right-0 top-0 h-full w-full sm:w-[440px] bg-white border-l shadow-2xl z-50 overflow-y-auto" data-testid="reservation-form-panel">
      <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between z-10">
        <h3 className="font-bold text-slate-900">Faire une réservation</h3>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-reservation-form"><XIcon className="h-4 w-4" /></Button>
      </div>
      <div className="p-5 space-y-3">
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Entreprise *</label>
          <input value={form.entreprise} onChange={(e) => setForm(p => ({ ...p, entreprise: e.target.value }))} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="res-entreprise" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Contact</label>
            <input value={form.contact} onChange={(e) => setForm(p => ({ ...p, contact: e.target.value }))} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Responsable</label>
            <input value={form.responsable} onChange={(e) => setForm(p => ({ ...p, responsable: e.target.value }))} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Type de panneau</label>
          <select value={form.type_panneau} onChange={(e) => setForm(p => ({ ...p, type_panneau: e.target.value, emplacement_id: '' }))} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="res-type">
            <option value="Panneau déroulant">Panneau déroulant</option>
            <option value="Panneau LED">Panneau LED (intérieur)</option>
            <option value="Grand format">Grand format</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Emplacement *</label>
          <select value={form.emplacement_id} onChange={(e) => setForm(p => ({ ...p, emplacement_id: e.target.value }))} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="res-emplacement">
            <option value="">-- Choisir un emplacement --</option>
            {filteredEmps.map(e => (
              <option key={e.id} value={e.id}>{`#${e.numero || '?'} · ${e.nom}${isUnavailable(e) ? ' ⚠ (déjà réservé sur contrat)' : ''}`}</option>
            ))}
          </select>
          {empUnavailable && (
            <div className="mt-1 text-xs bg-amber-50 border border-amber-300 text-amber-800 rounded p-2">
              ⚠ Cet emplacement est déjà utilisé par un contrat actif. La réservation restera possible mais peut créer un conflit.
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Nb d&apos;affiches</label>
            <input type="number" min="1" max="10" value={form.nb_affiches} onChange={(e) => setForm(p => ({ ...p, nb_affiches: e.target.value }))} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Montant (FCFA)</label>
            <input value={form.montant} onChange={(e) => setForm(p => ({ ...p, montant: e.target.value }))} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Du *</label>
            <input type="date" value={form.date_debut} onChange={(e) => setForm(p => ({ ...p, date_debut: e.target.value }))} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="res-date-debut" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Au *</label>
            <input type="date" value={form.date_fin} onChange={(e) => setForm(p => ({ ...p, date_fin: e.target.value }))} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="res-date-fin" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Motif / Note</label>
          <textarea value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} rows={3} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
        </div>
        <Button onClick={submit} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 h-11 gap-2" data-testid="submit-reservation">
          <Plus className="h-4 w-4" /> {saving ? 'Enregistrement…' : 'Créer la réservation'}
        </Button>
      </div>
    </div>
  );
}
